/**
 * React hooks for the runtime plugin registry.
 *
 *   usePlugins()         - current list of loaded plugins, re-renders on
 *                          registry changes
 *   usePluginsForSlot()  - subset filling a specific UI slot
 *   usePluginResult()    - fetches /result/{studyId} from one plugin,
 *                          returns {data, loading, error}
 *   useAutoLoad()        - fired once at app boot to pull userSettings
 *                          and populate the registry
 */

import { useEffect, useState } from 'react';
import { getPluginResult, type TriageResult } from './client';
import {
  loadRuntimePlugins,
  pluginsForSlot,
  snapshot,
  subscribe,
} from './registry';
import { loadAllowedPluginIds, loadPluginConfigs } from './settingsBridge';
import type { LoadedRuntimePlugin, UiSlot } from './types';

/** All plugins currently in the registry. Re-renders when loadRuntimePlugins
 *  / reloadPlugin / unloadPlugin fire. */
export function usePlugins(): LoadedRuntimePlugin[] {
  const [list, setList] = useState<LoadedRuntimePlugin[]>(snapshot);
  useEffect(() => subscribe(() => setList(snapshot())), []);
  return list;
}

/** Plugins filling a given slot, in registration order. */
export function usePluginsForSlot(slot: UiSlot): LoadedRuntimePlugin[] {
  const [list, setList] = useState<LoadedRuntimePlugin[]>(() => pluginsForSlot(slot));
  useEffect(
    () => subscribe(() => setList(pluginsForSlot(slot))),
    [slot],
  );
  return list;
}

/** Fetch result for one study from one plugin. Returns a loading state
 *  while in flight. If pollMs > 0, re-fetches on that interval until
 *  status !== 'pending' — useful for the worklist badge that wants to
 *  show the result as soon as the sidecar has it. */
export function usePluginResult(
  plugin: LoadedRuntimePlugin | undefined,
  studyId: string | undefined,
  pollMs = 0,
): { data: TriageResult | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<TriageResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!plugin || !studyId || !plugin.loaded) {
      setData(null);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const fetchOne = async () => {
      setLoading(true);
      try {
        const r = await getPluginResult(plugin, studyId);
        if (cancelled) return;
        setData(r);
        setError(null);
        if (pollMs > 0 && r.status === 'pending') {
          timer = setTimeout(fetchOne, pollMs);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchOne();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [plugin?.manifest.id, studyId, pollMs]);

  return { data, loading, error };
}

/** Fire once at app boot. Pulls userSettings.plugins, calls each sidecar's
 *  /manifest, and populates the registry. Safe to call multiple times —
 *  loadRuntimePlugins fully replaces registry state each call. */
export function useAutoLoadPlugins(): { loading: boolean; error: string | null } {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [configs, allowed] = await Promise.all([
          loadPluginConfigs(),
          loadAllowedPluginIds(),
        ]);
        // Enforce vendor allowlist. Admin-added entries for plugin ids
        // the vendor hasn't licensed are silently dropped from the
        // active registry (they stay in userSettings but don't surface).
        const filtered = allowed.size === 0
          ? []  // No allowlist = nothing active (vendor hasn't enabled anything yet)
          : configs.filter((c) => allowed.has(c.id));
        if (!cancelled) await loadRuntimePlugins(filtered);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return { loading, error };
}
